import type { ReactNode } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';

import type { CredentialingStatus } from '../../types';
import { overlineSx, wf } from '../shell/tokens';

import { CREDENTIALING_STATUS_LABELS } from './credentialingStatus';

export interface CredentialingPayerJumpEntry {
  key: string;
  name: ReactNode;
  /** One status per provider row shown for this payer; a row with no record is "not_started". */
  statuses: CredentialingStatus[];
}

/** Rejections first: they are what staff come here to find. */
const TALLY_ORDER: CredentialingStatus[] = ['rejected', 'in_progress', 'complete', 'not_started'];

function tally(statuses: CredentialingStatus[]) {
  return TALLY_ORDER.flatMap(status => {
    const n = statuses.filter(item => item === status).length;
    return n ? [`${n} ${CREDENTIALING_STATUS_LABELS[status].toLowerCase()}`] : [];
  }).join(' · ');
}

/**
 * One chip per payer above the staff credentialing records. A click takes the
 * caller to that payer's rows, so a payer's status and rejection detail are one
 * click from the top of the tab. Purely presentational; the caller owns the jump.
 */
export function CredentialingPayerJumpStrip({
  entries,
  onJump,
}: {
  entries: CredentialingPayerJumpEntry[];
  onJump: (key: string) => void;
}) {
  return (
    <Box component="nav" aria-label="Jump to a payer">
      <Typography sx={overlineSx}>Jump to a payer</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', mt: '8px' }}>
        {entries.map(entry => {
          const rejected = entry.statuses.includes('rejected');
          return (
            <ButtonBase
              key={entry.key}
              onClick={() => onJump(entry.key)}
              sx={{
                flexDirection: 'column',
                alignItems: 'flex-start',
                textAlign: 'left',
                padding: '8px 12px',
                borderRadius: '8px',
                border: `1px solid ${rejected ? wf.red : wf.line}`,
                backgroundColor: rejected ? wf.redSoft : wf.surface,
                '&:hover': { borderColor: rejected ? wf.red : wf.blueLine },
                '&.Mui-focusVisible': { outline: `2px solid ${wf.blue}`, outlineOffset: '2px' },
              }}
            >
              <Typography sx={{ fontSize: '13px', fontWeight: 700, color: wf.ink }}>
                {entry.name}
              </Typography>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: rejected ? 600 : 400,
                  color: rejected ? wf.red : wf.muted,
                }}
              >
                {tally(entry.statuses)}
              </Typography>
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
}
